import React, { useId } from 'react';
import { useFieldContext } from './FormField';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  endAdornment?: React.ReactNode;
}

export function Input({ error, endAdornment, className = '', id, ...rest }: InputProps) {
  const field = useFieldContext();
  const localErrorId = useId();
  const errorId = field?.errorId ?? localErrorId;
  const invalid = Boolean(error) || Boolean(field?.hasError);
  const describedBy = [
    rest['aria-describedby'],
    field?.hasHint ? field.hintId : undefined,
    invalid ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const input = (
    <input
      {...rest}
      id={id ?? field?.inputId}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy || undefined}
      className={[
        'w-full px-3 py-2 rounded-lg border text-text-dark placeholder-text-light bg-white transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate',
        invalid ? 'border-danger' : 'border-champagne-dark hover:border-chocolate/60',
        endAdornment ? 'pr-12' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );

  return (
    <div className="flex flex-col gap-1">
      {endAdornment ? (
        <div className="relative">
          {input}
          <div className="absolute inset-y-0 right-1 flex items-center">{endAdornment}</div>
        </div>
      ) : (
        input
      )}
      {error && (
        <span id={errorId} className="text-danger text-sm">
          {error}
        </span>
      )}
    </div>
  );
}
